import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Modal, Spinner } from "react-bootstrap";
import { FiArchive, FiAward, FiBookOpen, FiCalendar, FiCheck, FiCheckCircle, FiChevronLeft, FiChevronRight, FiCopy, FiDownload, FiEdit2, FiFileText, FiFlag, FiGitBranch, FiLayers, FiLogIn, FiMail, FiMapPin, FiMessageSquare, FiPlus, FiRefreshCw, FiRotateCcw, FiSearch, FiUpload, FiUser, FiUsers, FiXCircle } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import PersonAvatar from "../../components/0. Utilidades/PersonAvatar";
import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import SecLogo from "../../static/Logo-SEC.jpg";
import chileRegionPaths from "./chileRegionPaths";
import "./PersonasPage.css";

const display = (value) => value === null || value === undefined || value === "" ? "—" : value;
const booleanLabel = (value) => value === true ? "Sí" : value === false ? "No" : "—";
const AI_STORY_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const orderByProfileOpenings = (left, right) => (
  (Number(right.visitas_30_dias) || 0) - (Number(left.visitas_30_dias) || 0)
  || (Number(right.ultima_apertura_local) || 0) - (Number(left.ultima_apertura_local) || 0)
);
const readableDate = (value) => {
  if (!value) return "Sin fecha";
  const raw = String(value);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(raw);
  return date.toLocaleDateString("es-CL");
};
const hasYetToOccur = (value) => {
  if (!value) return false;
  const raw = String(value);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(raw);
  if (Number.isNaN(date.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date > today;
};
const ageFromBirthDate = (value) => {
  if (!value) return null;
  const raw = String(value);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(raw);
  if (Number.isNaN(date.getTime()) || date > new Date()) return null;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  if (today.getMonth() < date.getMonth() || (today.getMonth() === date.getMonth() && today.getDate() < date.getDate())) age -= 1;
  return age;
};
const statusFilters = [
  ["todas", "Todas"],
  ["ok", "OK"],
  ["problema", "Problema"],
];
const editableFields = [
  ["nombre", "Nombre completo", "text"],
  ["celular", "Celular", "text"],
  ["correo", "Correo", "email"],
  ["comuna", "Comuna", "text"],
];
const informationFields = {
  nacimiento: { label: "Fecha de nacimiento", type: "date" },
  genero: {
    label: "Género",
    type: "choice",
    options: [
      { value: "Femenino", label: "Femenino" },
      { value: "Masculino", label: "Masculino" },
    ],
  },
  nacionalidad: { label: "Nacionalidad", type: "text" },
  estado_civil: { label: "Estado civil", type: "text" },
  estudios: { label: "Nivel de estudios", type: "text" },
  situacion_laboral: { label: "Situación laboral", type: "text" },
  ingresos: { label: "Rango de ingresos", type: "text" },
  autoriza_compartir_informacion: { label: "Autoriza compartir información", type: "text" },
  certificacion_sec: { label: "Certificación SEC", type: "boolean" },
  direccion: { label: "Dirección", type: "text" },
  territorio: { label: "Sectores donde trabaja", type: "text" },
  licencia_conducir: { label: "Tiene licencia de conducir", type: "boolean" },
  movilizacion_propia: { label: "Movilización propia", type: "text" },
};
const buildEditForm = (person) => Object.fromEntries(
  editableFields.map(([field]) => [field, person[field] ?? ""]),
);

const InfoItem = ({ label, value, copyable = false, onEdit = null }) => {
  const [copied, setCopied] = useState(false);
  const displayedValue = display(value);
  const copyValue = value === null || value === undefined ? "" : String(value);

  const copy = async () => {
    if (!copyValue) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="person-detail-item">
      <div className="person-detail-item-heading">
        <span>{label}</span>
        <div className="person-detail-item-actions">
          {copyable && copyValue && <button type="button" onClick={copy} title={copied ? "Copiado" : `Copiar ${label.toLowerCase()}`} aria-label={copied ? `${label} copiado` : `Copiar ${label.toLowerCase()}`}>{copied ? <FiCheck /> : <FiCopy />}</button>}
          {onEdit && <button type="button" onClick={onEdit} title={`Editar ${label.toLowerCase()}`} aria-label={`Editar ${label.toLowerCase()}`}><FiEdit2 /></button>}
        </div>
      </div>
      <strong title={displayedValue}>{displayedValue}</strong>
    </div>
  );
};

const FittedPersonName = ({ children }) => {
  const nameRef = useRef(null);

  useLayoutEffect(() => {
    const element = nameRef.current;
    if (!element) return undefined;
    const container = element.parentElement;
    let animationFrame = null;
    let cancelled = false;

    const fitName = () => {
      if (cancelled) return;
      element.style.fontSize = "";
      const availableWidth = element.clientWidth;
      const maximumSize = Number.parseFloat(window.getComputedStyle(element).fontSize);
      if (!availableWidth || !maximumSize || element.scrollWidth <= availableWidth) return;

      let minimum = 8;
      let maximum = maximumSize;
      let fittedSize = minimum;
      for (let step = 0; step < 10; step += 1) {
        const candidate = (minimum + maximum) / 2;
        element.style.fontSize = `${candidate}px`;
        if (element.scrollWidth <= availableWidth) {
          fittedSize = candidate;
          minimum = candidate;
        } else {
          maximum = candidate;
        }
      }
      element.style.fontSize = `${Math.max(8, fittedSize * 0.98)}px`;
    };

    const scheduleFit = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(fitName);
    };
    scheduleFit();
    document.fonts?.ready.then(scheduleFit);
    const observer = new ResizeObserver(scheduleFit);
    if (container) observer.observe(container);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [children]);

  return <h2 ref={nameRef} title={children}>{children}</h2>;
};

const ChileRegionMap = ({ region, comuna, trades = [], tradeOptions = [], selectedTradeIds = [] }) => {
  const normalizedRegion = normalize(region);
  const selectedIds = new Set(selectedTradeIds.map(Number));
  const displayedTrades = tradeOptions.length > 0
    ? tradeOptions
    : trades.map((nombre, index) => ({ id: `selected-${index}`, nombre, selected: true }));
  const activeRegionData = chileRegionPaths.find(({ aliases }) => (
    aliases.some((alias) => normalizedRegion === alias || normalizedRegion.includes(alias))
  ));
  const activeRegion = activeRegionData?.name || null;
  const tooltipText = comuna || activeRegion || "Comuna sin registrar";
  const regionLabel = display(region);
  const regionSubtitle = normalize(regionLabel).startsWith("region ")
    ? regionLabel
    : `Región ${regionLabel}`;
  const tooltipPosition = activeRegionData?.labelPosition ?? 50;

  return (
    <section className="person-chile-map" aria-label={`Ubicación territorial: ${display(region)}, ${display(comuna)}`}>
      <div className="person-chile-map-trades">
        <span>Oficios</span>
        {displayedTrades.length > 0 ? <div>{displayedTrades.map((trade) => {
          const selected = trade.selected || selectedIds.has(Number(trade.id));
          return <Badge key={trade.id} className={selected ? "is-selected" : ""} title={`${trade.maestras_count || 0} maestras asociadas`}>{trade.nombre}</Badge>;
        })}</div> : <small>No hay oficios configurados.</small>}
      </div>
      <div className="person-chile-map-heading">
        <span>Ubicación territorial</span>
        <strong>{regionSubtitle}</strong>
      </div>
      <div className="person-chile-map-canvas">
        <div
          className="person-chile-map-tooltip"
          style={{ left: `clamp(70px, ${tooltipPosition}%, calc(100% - 70px))` }}
          role="note"
        >
          {tooltipText}
        </div>
        <svg
          viewBox="0 20 1000 100"
          preserveAspectRatio="none"
          role="img"
          aria-label={`Mapa compacto de Chile. Región destacada: ${activeRegion || "sin región reconocida"}.`}
        >
          {chileRegionPaths.map(({ code, name, d }) => {
            const active = code === activeRegionData?.code;
            return <path
              key={code}
              className={`person-chile-region${active ? " active" : ""}`}
              d={d}
              tabIndex={active ? 0 : undefined}
              aria-label={active ? `${name}: ${tooltipText}` : name}
            ><title>{active ? `${name}: ${tooltipText}` : name}</title></path>;
          })}
        </svg>
      </div>
    </section>
  );
};

export const PersonaDetail = ({ person, onPersonUpdate }) => {
  const info = person;
  const labels = person.labels || {};
  const labeled = (field) => labels[field] || info[field];
  const trades = person.oficios_detalle || [];
  const whatsappGroups = person.grupos_whatsapp || [];
  const [showGroups, setShowGroups] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState(whatsappGroups.map((group) => group.id));
  const [groupQuery, setGroupQuery] = useState("");
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsSaving, setGroupsSaving] = useState(false);
  const [groupsError, setGroupsError] = useState("");
  const [generatingStory, setGeneratingStory] = useState(false);
  const [storyError, setStoryError] = useState("");
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [documentFile, setDocumentFile] = useState(null);
  const [savingDocument, setSavingDocument] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [activatingPerson, setActivatingPerson] = useState(false);
  const [activationError, setActivationError] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [storyExpanded, setStoryExpanded] = useState(false);
  const [storyCanExpand, setStoryCanExpand] = useState(false);
  const storyTextRef = useRef(null);
  const [activeTab, setActiveTab] = useState("perfil");
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(() => buildEditForm(person));
  const [editTradeIds, setEditTradeIds] = useState(person.oficios || []);
  const [tradeOptions, setTradeOptions] = useState([]);
  const [informationOptions, setInformationOptions] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [revertingPerson, setRevertingPerson] = useState(false);
  const [editError, setEditError] = useState("");
  const [informationEdit, setInformationEdit] = useState(null);
  const [informationValue, setInformationValue] = useState("");
  const [savingInformation, setSavingInformation] = useState(false);
  const [informationError, setInformationError] = useState("");
  const storyStartedAt = info.bios_ai_update_started_at ? new Date(info.bios_ai_update_started_at) : null;
  const storyInProgress = storyStartedAt && !Number.isNaN(storyStartedAt.getTime()) && Date.now() - storyStartedAt.getTime() < 30 * 60 * 1000;
  const storyUpdatedAt = info.bios_ai_updated_at ? new Date(info.bios_ai_updated_at) : null;
  const storyAvailableAt = storyUpdatedAt && !Number.isNaN(storyUpdatedAt.getTime())
    ? new Date(storyUpdatedAt.getTime() + AI_STORY_COOLDOWN_MS)
    : null;
  const storyOnCooldown = Boolean(storyAvailableAt && storyAvailableAt.getTime() > Date.now());
  const storyButtonTitle = storyOnCooldown
    ? `Disponible nuevamente desde el ${storyAvailableAt.toLocaleDateString("es-CL")}`
    : "Generar historia con AI";
  const age = ageFromBirthDate(person.nacimiento);
  const storyText = generatingStory ? "Generando historia de vida con AI..." : info.bios || "Todavía no hay una historia de vida registrada.";

  useEffect(() => {
    setSelectedGroupIds((person.grupos_whatsapp || []).map((group) => group.id));
    setActiveTab("perfil");
  }, [person.id, person.grupos_whatsapp]);

  useEffect(() => {
    const controller = new AbortController();
    fetch(getApiUrl("api/perfiles/oficios/"), {
      credentials: "include",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error();
        return response.json();
      })
      .then((data) => setTradeOptions(Array.isArray(data) ? data : data.results || []))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setTradeOptions([]);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetch(getApiUrl("api/listas/comunidades-basic-options/"), { credentials: "include", signal: controller.signal }),
      fetch(getApiUrl("api/listas/maestra-second-step-options/"), { credentials: "include", signal: controller.signal }),
    ])
      .then(async ([basicResponse, secondResponse]) => {
        if (!basicResponse.ok || !secondResponse.ok) throw new Error();
        return Promise.all([basicResponse.json(), secondResponse.json()]);
      })
      .then(([basic, second]) => setInformationOptions({
        comuna: basic.comuna || [],
        genero: basic.genero || [],
        nacionalidad: basic.nacionalidad || [],
        estado_civil: basic.e_civil || [],
        estudios: basic.estudios || [],
        situacion_laboral: second.situacion_laboral || [],
        ingresos: second.ingresos || [],
        autoriza_compartir_informacion: second.b_trabajo || [],
        territorio: second.territorio || [],
        movilizacion_propia: second.m_propia || [],
      }))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setInformationOptions({});
      });
    return () => controller.abort();
  }, []);

  useEffect(() => { setStoryExpanded(false); }, [person.id, storyText]);

  useLayoutEffect(() => {
    const element = storyTextRef.current;
    if (!element || storyExpanded) return undefined;
    const measure = () => setStoryCanExpand(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [storyExpanded, storyText]);

  const openEditor = async () => {
    setEditForm(buildEditForm(person));
    setEditTradeIds(person.oficios || []);
    setEditError("");
    setShowEdit(true);
    if (tradeOptions.length) return;
    try {
      const response = await fetch(getApiUrl("api/perfiles/oficios/"), { credentials: "include" });
      if (!response.ok) throw new Error();
      const data = await response.json();
      setTradeOptions(Array.isArray(data) ? data : data.results || []);
    } catch (_error) {
      setEditError("No se pudieron cargar los oficios. Puedes editar los demás datos igualmente.");
    }
  };

  const savePerson = async () => {
    setSavingEdit(true);
    setEditError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/personas/${person.id}/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({
          ...editForm,
          oficios: editTradeIds,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const firstError = Object.values(data)[0];
        throw new Error(Array.isArray(firstError) ? firstError[0] : firstError || data.detail || "No se pudo guardar la ficha.");
      }
      onPersonUpdate?.(data);
      setShowEdit(false);
    } catch (requestError) {
      setEditError(String(requestError.message || requestError));
    } finally {
      setSavingEdit(false);
    }
  };

  const revertToInterested = async () => {
    const confirmed = window.confirm(
      `¿Estás segura de revertir a ${person.nombre} a Interesada?\n\nDejará de aparecer en Maestras, pero conservará toda su ficha e historial.`,
    );
    if (!confirmed) return;
    setRevertingPerson(true);
    setEditError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/personas/${person.id}/revertir-interesada/`), {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.detail || "No se pudo revertir la clasificación.");
      setShowEdit(false);
      onPersonUpdate?.(data);
    } catch (requestError) {
      setEditError(String(requestError.message || requestError));
    } finally {
      setRevertingPerson(false);
    }
  };

  const openInformationEditor = (field) => {
    const configuration = informationFields[field];
    if (!configuration) return;
    setInformationEdit({ field, ...configuration });
    setInformationValue(
      configuration.type === "boolean"
        ? person[field] === null || person[field] === undefined
          ? ""
          : String(person[field])
        : person[field] ?? "",
    );
    setInformationError("");
  };

  const saveInformationField = async () => {
    if (!informationEdit) return;
    setSavingInformation(true);
    setInformationError("");
    try {
      const csrfToken = await getCsrfToken();
      let value = informationValue;
      if (["date", "boolean"].includes(informationEdit.type) && value === "") value = null;
      else if (informationEdit.type === "boolean") value = value === "true";
      const response = await fetch(getApiUrl(`api/personas/${person.id}/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ [informationEdit.field]: value }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const firstError = Object.values(data)[0];
        throw new Error(Array.isArray(firstError) ? firstError[0] : firstError || data.detail || "No se pudo actualizar este dato.");
      }
      onPersonUpdate?.(data);
      setInformationEdit(null);
    } catch (requestError) {
      setInformationError(String(requestError.message || requestError));
    } finally {
      setSavingInformation(false);
    }
  };

  const openGroupEditor = async () => {
    setShowGroups(true);
    setGroupsError("");
    if (groupOptions.length) return;
    setGroupsLoading(true);
    try {
      const response = await fetch(getApiUrl("api/whatsapp/groups/"), { credentials: "include" });
      if (!response.ok) throw new Error();
      setGroupOptions(await response.json());
    } catch (_error) {
      setGroupsError("No se pudieron cargar los grupos de WhatsApp.");
    } finally {
      setGroupsLoading(false);
    }
  };

  const saveGroups = async () => {
    setGroupsSaving(true);
    setGroupsError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/whatsapp/personas/${person.id}/groups/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ group_ids: selectedGroupIds }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudieron guardar los grupos.");
      onPersonUpdate?.({
        ...person,
        grupos_whatsapp: data.grupos_whatsapp || [],
        es_comunidad: Boolean(data.es_comunidad),
      });
      setShowGroups(false);
    } catch (requestError) {
      setGroupsError(requestError.message);
    } finally {
      setGroupsSaving(false);
    }
  };

  const generateStory = async () => {
    if (!window.confirm("¿Generar la historia de vida con AI? Esta acción reemplazará el resumen actual y solo podrá repetirse después de 30 días.")) return;
    setGeneratingStory(true);
    setStoryError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/personas/${person.id}/generar-historia-ai/`), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo generar la historia de vida.");
      onPersonUpdate?.(data);
    } catch (requestError) {
      setStoryError(requestError.message);
    } finally {
      setGeneratingStory(false);
    }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    setSavingComment(true);
    setCommentError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/personas/${person.id}/comments/`), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ comment_text: commentText.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo guardar el comentario.");
      onPersonUpdate?.(data);
      setCommentText("");
      setShowCommentForm(false);
    } catch (requestError) {
      setCommentError(requestError.message);
    } finally {
      setSavingComment(false);
    }
  };

  const addDocument = async () => {
    if (!documentFile) return;
    setSavingDocument(true);
    setDocumentError("");
    try {
      const csrfToken = await getCsrfToken();
      const formData = new FormData();
      formData.append("documento", documentFile);
      formData.append("nombre", documentName.trim());
      const response = await fetch(getApiUrl(`api/personas/${person.id}/documents/`), {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "No se pudo subir el documento.");
      onPersonUpdate?.(data);
      setDocumentName("");
      setDocumentFile(null);
      setShowDocumentForm(false);
    } catch (requestError) {
      setDocumentError(requestError.message);
    } finally {
      setSavingDocument(false);
    }
  };

  const toggleTrackingStatus = async () => {
    setSavingStatus(true);
    setActivationError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/personas/${person.id}/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ estado: person.estado === "problema" ? "ok" : "problema" }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "No se pudo cambiar el estado.");
      onPersonUpdate?.(data);
    } catch (requestError) {
      setActivationError(requestError.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const archivePerson = async () => {
    setActivatingPerson(true);
    setActivationError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/personas/${person.id}/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ activa: false }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "No se pudo archivar la persona.");
      onPersonUpdate?.(data);
    } catch (requestError) {
      setActivationError(requestError.message);
    } finally {
      setActivatingPerson(false);
    }
  };

  const visibleGroupOptions = groupOptions.filter((group) => normalize(group.nombre).includes(normalize(groupQuery)));
  const initiativeHistory = person.iniciativas || [];
  const programHistory = initiativeHistory.filter((initiative) => normalize(initiative.tipo) === "programa");
  const generalInitiativeHistory = initiativeHistory.filter((initiative) => normalize(initiative.tipo) !== "programa");
  const initiativeHref = (initiative) => {
    const type = normalize(initiative.tipo);
    if (type === "programa") return `/programas/${initiative.id}`;
    if (type === "proyecto") return `/proyectos/${initiative.id}`;
    return `/iniciativas/${initiative.id}`;
  };
  const initiativeList = (initiatives, emptyMessage) => initiatives.length === 0
    ? <p className="person-detail-empty">{emptyMessage}</p>
    : (
      <div className="person-initiative-history">
        {initiatives.map((initiative) => {
          const upcoming = hasYetToOccur(initiative.f_inicio);
          return (
            <a
              key={initiative.id}
              href={initiativeHref(initiative)}
              className={upcoming ? "is-upcoming" : initiative.aprobada ? "is-approved" : "is-not-approved"}
            >
              <div>
                <span className="person-history-type">{initiative.tipo}</span>
                <strong>{initiative.nombre}</strong>
                <small><FiCalendar /> {readableDate(initiative.f_inicio)}</small>
              </div>
              <div className="person-history-statuses">
                {!upcoming && <span className={`person-history-result ${initiative.aprobada ? "is-approved" : "is-not-approved"}`}>
                  {initiative.aprobada ? "Aprobada" : "No aprobada"}
                </span>}
                {initiative.inscrita && <span>Inscrita</span>}
                {initiative.aporte_realizado && <span>Aporte realizado</span>}
                {(initiative.asistencias || []).length > 0 && <span title={initiative.asistencias.map(readableDate).join(", ")}>{initiative.asistencias.length} {(initiative.asistencias || []).length === 1 ? "asistencia" : "asistencias"}</span>}
                {(initiative.excusas || []).length > 0 && <span title={initiative.excusas.map((excuse) => `${readableDate(excuse.fecha)}: ${excuse.texto}`).join("\n")}>{initiative.excusas.length} {(initiative.excusas || []).length === 1 ? "excusa" : "excusas"}</span>}
              </div>
            </a>
          );
        })}
      </div>
    );
  const trajectoryStats = (initiatives) => {
    const enrolled = initiatives.filter((initiative) => initiative.inscrita).length;
    const approved = initiatives.filter((initiative) => initiative.inscrita && initiative.aprobada).length;
    return {
      enrolled,
      approvalRate: enrolled > 0 ? Math.round((approved / enrolled) * 100) : null,
    };
  };
  const trajectoryStatsBoxes = (initiatives) => {
    const stats = trajectoryStats(initiatives);
    return (
      <div className="person-history-stats" aria-label="Estadísticas de trayectoria">
        <div><span>Inscritas</span><strong>{stats.enrolled}</strong></div>
        <div><span>Tasa de aprobación</span><strong>{stats.approvalRate === null ? "—" : `${stats.approvalRate}%`}</strong></div>
      </div>
    );
  };
  const entryMethods = person.metodo_ingreso?.metodos || [];
  const tabs = [
    ["perfil", "Información"],
    ["trayectoria", `Trayectoria (${(person.iniciativas || []).length})`],
    ["comentarios", `Comentarios (${(person.comentarios || []).length})`],
    ["documentos", `Documentos (${(person.documentos || []).length})`],
    ["registros", `Registros (${(person.postulaciones_historicas || []).length + (person.registros_interesada || []).length})`],
  ];
  return (
    <div className="person-detail">
      <header className="person-detail-header">
        <div className="person-detail-avatar-frame">
          <div className="person-detail-avatar">
            <PersonAvatar src={info.imagen} name={person.nombre} alt={`Fotografía de ${person.nombre}`} tone={whatsappGroups.length ? "comunidad" : person.tipo === "maestra" ? "maestra" : "persona"} />
          </div>
          {info.certificacion_sec && <img className="person-sec-mark person-sec-mark--detail" src={SecLogo} alt="Certificación SEC" title="Certificación SEC" />}
        </div>
        <div className="person-detail-heading">
          <div>
            <div className="person-role-list">
              <Badge className={`person-lifecycle-badge person-lifecycle-badge--${person.tipo === "maestra" ? "maestra" : "interesada"}`}>{person.tipo === "maestra" ? "Maestra" : "Interesada"}</Badge>
              <Badge className={`person-status-badge person-status-badge--${person.estado === "problema" ? "problema" : "ok"}`}>{person.estado === "problema" ? "Problema" : "OK"}</Badge>
            </div>
            <FittedPersonName>{person.nombre}</FittedPersonName>
            <p>{person.rut}</p>
          </div>
          <div className="person-heading-actions">
            <button type="button" onClick={openEditor} disabled={savingEdit || revertingPerson || savingStatus || activatingPerson} title="Editar ficha"><FiEdit2 /> Editar</button>
            <button type="button" onClick={toggleTrackingStatus} disabled={savingEdit || savingStatus || activatingPerson} title={person.estado === "problema" ? "Marcar como OK" : "Marcar como problema"}>{savingStatus ? <Spinner animation="border" size="sm" /> : <FiFlag />} Marcar</button>
            <button type="button" onClick={archivePerson} disabled={!person.activa || savingEdit || savingStatus || activatingPerson} title="Archivar ficha">{activatingPerson ? <Spinner animation="border" size="sm" /> : <FiArchive />} Archivar</button>
          </div>
        </div>
      </header>
      {activationError && <p className="person-inline-error person-activation-error">{activationError}</p>}

      <section className="person-key-facts person-key-facts--compact" aria-label="Información principal">
        <InfoItem label="Correo" value={info.correo} copyable />
        <InfoItem label="Celular" value={info.celular} copyable />
      </section>

      <div className="person-whatsapp-block person-whatsapp-block--summary">
        <div className="person-section-row"><div><span>WhatsApp</span><strong><FiUsers /> Grupos de WhatsApp</strong></div><button type="button" onClick={openGroupEditor}><FiEdit2 /> Editar</button></div>
        {whatsappGroups.length ? <ul>{whatsappGroups.map((group) => <li key={group.id}>{group.nombre}</li>)}</ul> : <p className="person-detail-empty">No pertenece a ningún grupo.</p>}
      </div>

      <ChileRegionMap
        region={info.region}
        comuna={labeled("comuna")}
        trades={trades}
        tradeOptions={tradeOptions}
        selectedTradeIds={person.oficios || []}
      />

      <section className="person-detail-story">
        <div className="person-story-header">
          <div><span>Resumen</span><h3>Historia de vida</h3></div>
          {person.tipo === "maestra" && <button type="button" onClick={generateStory} disabled={generatingStory || storyOnCooldown || Boolean(storyInProgress)} title={storyButtonTitle}><FiRefreshCw className={generatingStory ? "spin" : ""} /> {generatingStory ? "Generando" : "Generar con AI"}</button>}
        </div>
        {storyError && <p className="person-inline-error">{storyError}</p>}
        <div className={`person-story-content${storyCanExpand ? " has-overflow" : ""}${storyExpanded ? " expanded" : ""}`}>
          <p ref={storyTextRef} className={storyExpanded ? "" : "is-collapsed"}>{storyText}</p>
        </div>
        {storyCanExpand && <button type="button" className="person-story-more" onClick={() => setStoryExpanded((current) => !current)} aria-expanded={storyExpanded}>{storyExpanded ? "Ver menos" : "Ver más"}</button>}
      </section>

      <section className="person-detail-tabs-shell">
        <nav className="person-detail-tabs" aria-label="Secciones de la ficha">
          {tabs.map(([key, label]) => <button key={key} type="button" className={activeTab === key ? "active" : ""} onClick={() => setActiveTab(key)}>{label}</button>)}
        </nav>
        <div className="person-tab-panel">
        {activeTab === "perfil" && <section className="person-info-groups">
          <div className="person-info-group">
            <h3><FiUser /> Datos personales</h3>
            <div className="person-detail-grid">
              <InfoItem label="Fecha de nacimiento" value={readableDate(person.nacimiento)} onEdit={() => openInformationEditor("nacimiento")} />
              <InfoItem label="Edad" value={age !== null ? `${age} años` : null} onEdit={() => openInformationEditor("nacimiento")} />
              <InfoItem label="Género" value={person.genero} onEdit={() => openInformationEditor("genero")} />
              <InfoItem label="Nacionalidad" value={labeled("nacionalidad")} onEdit={() => openInformationEditor("nacionalidad")} />
              <InfoItem label="Estado civil" value={labeled("estado_civil")} onEdit={() => openInformationEditor("estado_civil")} />
            </div>
          </div>

          <div className="person-info-group">
            <h3><FiBookOpen /> Formación y situación laboral</h3>
            <div className="person-detail-grid">
              <InfoItem label="Nivel de estudios" value={labeled("estudios")} onEdit={() => openInformationEditor("estudios")} />
              <InfoItem label="Situación laboral" value={labeled("situacion_laboral")} onEdit={() => openInformationEditor("situacion_laboral")} />
              <InfoItem label="Rango de ingresos" value={labeled("ingresos")} onEdit={() => openInformationEditor("ingresos")} />
              <InfoItem label="Autoriza compartir información" value={labeled("autoriza_compartir_informacion")} onEdit={() => openInformationEditor("autoriza_compartir_informacion")} />
              <InfoItem label="Certificación SEC" value={booleanLabel(info.certificacion_sec)} onEdit={() => openInformationEditor("certificacion_sec")} />
            </div>
          </div>

          <div className="person-info-group">
            <h3><FiMapPin /> Ubicación y movilidad</h3>
            <div className="person-detail-grid">
              <InfoItem label="Dirección" value={info.direccion} onEdit={() => openInformationEditor("direccion")} />
              <InfoItem label="Sectores donde trabaja" value={labeled("territorio")} onEdit={() => openInformationEditor("territorio")} />
              <InfoItem label="Tiene licencia de conducir" value={booleanLabel(info.licencia_conducir)} onEdit={() => openInformationEditor("licencia_conducir")} />
              <InfoItem label="Movilización propia" value={labeled("movilizacion_propia")} onEdit={() => openInformationEditor("movilizacion_propia")} />
            </div>
          </div>
        </section>}

        {activeTab === "trayectoria" && <section>
          <div className="person-history-section">
            <h3><FiAward /> Programas</h3>
            {trajectoryStatsBoxes(programHistory)}
            {initiativeList(programHistory, "No hay Programas asociados.")}
          </div>
          <div className="person-history-section">
            <h3><FiLayers /> Iniciativas</h3>
            {trajectoryStatsBoxes(generalInitiativeHistory)}
            {initiativeList(generalInitiativeHistory, "No hay Iniciativas asociadas.")}
          </div>
        </section>}

        {activeTab === "registros" && <section className="person-records-tab">
        <div className="person-records-section person-entry-method-section">
        <h3><FiLogIn /> Método de Ingreso</h3>
        <div className="person-entry-method-list">
          {entryMethods.map((method, index) => (
            <article key={`${method.tipo}-${method.iniciativa?.id || index}`} className={`person-entry-method person-entry-method--${method.tipo}`}>
              <div>
                <strong>{method.titulo}</strong>
                <p>{method.descripcion}</p>
              </div>
              {method.iniciativa && <a href={initiativeHref(method.iniciativa)}>Revisar {normalize(method.iniciativa.tipo) === "programa" ? "Programa" : "Iniciativa"}</a>}
            </article>
          ))}
        </div>
        </div>

        <div className="person-records-section">
        <h3><FiGitBranch /> Procesos de Selección</h3>
        {(person.postulaciones_historicas || []).length === 0 ? <p className="person-detail-empty">No hay postulaciones históricas registradas.</p> : (
          <div className="person-selection-history">
            {person.postulaciones_historicas.map((application) => <article key={application.id}>
              <div><span>{application.iniciativa_tipo || "Proceso de selección"}</span><strong>{application.iniciativa_nombre || application.proceso_nombre}</strong><small><FiCalendar /> {readableDate(application.fecha_postulacion)}</small></div>
              <dl>
                <div><dt>Postulación</dt><dd>{application.estado_postulacion || "—"}</dd></div>
                <div><dt>Entrevista</dt><dd>{application.estado_entrevista || "—"}</dd></div>
                <div><dt>Psicoanálisis</dt><dd>{application.estado_psicoanalisis || "—"}</dd></div>
                <div><dt>Privacidad</dt><dd>{application.consentimiento_privacidad ? "Consentimiento registrado" : "Sin consentimiento registrado"}</dd></div>
              </dl>
            </article>)}
          </div>
        )}
        </div>

        <div className="person-records-section">
        <h3><FiMail /> Newsletter</h3>
        {(person.registros_interesada || []).length === 0 ? <p className="person-detail-empty">No hay formularios de interesada registrados.</p> : (
          <div className="person-interest-history">
            {person.registros_interesada.map((record) => <article key={record.id} className={record.alerta_nombre ? "name-alert" : ""}>
              <div className="person-interest-title">
                <div><span className={record.formulario_avanzado ? "advanced" : "basic"}>{record.formulario_avanzado ? "Formulario avanzado" : "Formulario básico"}</span><strong>{record.nombre_origen || person.nombre}</strong><small><FiCalendar /> {readableDate(record.fecha_formulario_avanzado || record.fecha_registro)}</small></div>
                {record.alerta_nombre && <span className="person-name-alert">Revisar nombre · {Math.round((record.coincidencia_nombre || 0) * 100)}%</span>}
              </div>
              <dl>
                <div><dt>Tipo original</dt><dd>{record.tipo_origen || "—"}</dd></div>
                <div><dt>Oficio de interés</dt><dd>{record.oficio_interes || "—"}</dd></div>
                <div><dt>Formulario avanzado</dt><dd>{record.formulario_avanzado ? "Respondido" : "Pendiente"}</dd></div>
                <div><dt>Consentimientos</dt><dd>{record.consentimiento_basico ? "Básico" : "—"}{record.consentimiento_avanzado ? " + avanzado" : ""}</dd></div>
                <div><dt>Estado original</dt><dd>{record.estaba_activa ? "Activo" : "Inactivo"}</dd></div>
                <div><dt>Recordatorios enviados</dt><dd>{record.correos_formulario_avanzado || 0}</dd></div>
              </dl>
            </article>)}
          </div>
        )}
        </div>
        </section>}

        {activeTab === "comentarios" && <section>
        <div className="person-content-heading"><h3><FiMessageSquare /> Comentarios</h3><button type="button" onClick={() => setShowCommentForm((value) => !value)}><FiPlus /> Agregar</button></div>
        {showCommentForm && <div className="person-inline-form"><textarea rows="3" value={commentText} onChange={(event) => setCommentText(event.target.value)} placeholder="Escribe un comentario..." />{commentError && <p className="person-inline-error">{commentError}</p>}<div><button type="button" className="person-secondary-button" onClick={() => setShowCommentForm(false)}>Cancelar</button><button type="button" className="person-primary-button" disabled={savingComment || !commentText.trim()} onClick={addComment}>{savingComment ? "Guardando..." : "Guardar comentario"}</button></div></div>}
        {(person.comentarios || []).length === 0 ? <p className="person-detail-empty">No hay comentarios registrados.</p> : (
          <div className="person-comment-list">
            {person.comentarios.map((comment) => {
              const authorName = comment.created_by_user?.name || comment.autor || "Sin autor";
              return (
                <article key={comment.id}>
                  <p>{comment.comment_text}</p>
                  <div className="person-comment-author">
                    <PersonAvatar
                      src={comment.created_by_user?.image}
                      name={authorName}
                      alt={`Fotografía de ${authorName}`}
                      className="person-comment-author-avatar"
                      tone="profesional"
                    />
                    <small><strong>{authorName}</strong><span>{readableDate(comment.timestamp)}</span></small>
                  </div>
                </article>
              );
            })}
          </div>
        )}
        </section>}

        {activeTab === "documentos" && <section>
        <div className="person-content-heading"><h3><FiFileText /> Documentos</h3><button type="button" onClick={() => setShowDocumentForm((value) => !value)}><FiUpload /> Subir</button></div>
        {showDocumentForm && <div className="person-inline-form"><input type="text" value={documentName} onChange={(event) => setDocumentName(event.target.value)} placeholder="Nombre del documento (opcional)" /><input type="file" accept="application/pdf,.pdf" onChange={(event) => setDocumentFile(event.target.files?.[0] || null)} />{documentError && <p className="person-inline-error">{documentError}</p>}<div><button type="button" className="person-secondary-button" onClick={() => setShowDocumentForm(false)}>Cancelar</button><button type="button" className="person-primary-button" disabled={savingDocument || !documentFile} onClick={addDocument}>{savingDocument ? "Subiendo..." : "Subir documento"}</button></div></div>}
        {(person.documentos || []).length === 0 ? <p className="person-detail-empty">No hay documentos registrados.</p> : (
          <div className="person-document-list">
            {person.documentos.map((document) => <a key={document.id} href={document.documento} target="_blank" rel="noreferrer"><FiFileText /><span><strong>{document.nombre || "Documento"}</strong><small>{readableDate(document.timestamp)}</small></span><FiDownload /></a>)}
          </div>
        )}
        </section>}
        </div>
      </section>

      <Modal show={showGroups} onHide={() => setShowGroups(false)} centered dialogClassName="person-groups-modal">
        <Modal.Header closeButton><Modal.Title>Grupos de WhatsApp</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="person-groups-help">Selecciona los grupos de WhatsApp a los que pertenece esta Persona.</p>
          <label className="person-groups-search"><FiSearch /><input value={groupQuery} onChange={(event) => setGroupQuery(event.target.value)} placeholder="Buscar grupo..." /></label>
          {groupsError && <Alert variant="danger">{groupsError}</Alert>}
          {groupsLoading ? <div className="people-loading"><Spinner animation="border" size="sm" /></div> : <div className="person-groups-options">
            {visibleGroupOptions.map((group) => <label key={group.id}><input type="checkbox" checked={selectedGroupIds.includes(group.id)} onChange={() => setSelectedGroupIds((current) => current.includes(group.id) ? current.filter((id) => id !== group.id) : [...current, group.id])} /><span>{group.nombre}</span></label>)}
            {!visibleGroupOptions.length && <p className="person-detail-empty">No hay grupos que coincidan.</p>}
          </div>}
        </Modal.Body>
        <Modal.Footer><button type="button" className="person-secondary-button" onClick={() => setShowGroups(false)}>Cancelar</button><button type="button" className="person-primary-button" disabled={groupsSaving || groupsLoading} onClick={saveGroups}>{groupsSaving ? "Guardando..." : "Guardar grupos"}</button></Modal.Footer>
      </Modal>

      <Modal show={showEdit} onHide={() => { if (!savingEdit && !revertingPerson) setShowEdit(false); }} centered dialogClassName="person-edit-modal">
        <Modal.Header closeButton={!savingEdit && !revertingPerson}><Modal.Title>Editar ficha de persona</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="person-groups-help">Aquí puedes corregir únicamente los datos principales de contacto, ubicación y oficios.</p>
          <div className="person-edit-grid">
            {editableFields.map(([field, label, type]) => (
              <label key={field}>
                <span>{label}</span>
                {field === "comuna" && informationOptions.comuna?.length ? (
                  <select value={editForm[field]} onChange={(event) => setEditForm((current) => ({ ...current, [field]: event.target.value }))}>
                    <option value="">Selecciona una comuna</option>
                    {informationOptions.comuna.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                ) : (
                  <input
                    type={field === "celular" ? "tel" : type}
                    inputMode={field === "celular" ? "numeric" : undefined}
                    maxLength={field === "celular" ? 9 : undefined}
                    value={editForm[field]}
                    onChange={(event) => {
                      const value = field === "celular" ? event.target.value.replace(/\D/g, "").slice(0, 9) : event.target.value;
                      setEditForm((current) => ({ ...current, [field]: value }));
                    }}
                  />
                )}
              </label>
            ))}
          </div>
          <fieldset className="person-edit-trades">
            <legend>Oficios</legend>
            {tradeOptions.map((trade) => <label key={trade.id}><input type="checkbox" checked={editTradeIds.includes(trade.id)} onChange={() => setEditTradeIds((current) => current.includes(trade.id) ? current.filter((id) => id !== trade.id) : [...current, trade.id])} /><span>{trade.nombre}</span></label>)}
          </fieldset>
          {editError && <Alert variant="danger" className="mt-3">{editError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          {person.tipo === "maestra" && <button type="button" className="person-danger-button person-revert-button" disabled={savingEdit || revertingPerson} onClick={revertToInterested}>{revertingPerson ? <><Spinner animation="border" size="sm" /> Revirtiendo...</> : "Revertir a Interesada"}</button>}
          <button type="button" className="person-secondary-button" disabled={savingEdit || revertingPerson} onClick={() => setShowEdit(false)}>Cancelar</button>
          <button type="button" className="person-primary-button" disabled={savingEdit || revertingPerson || !editForm.nombre.trim()} onClick={savePerson}>{savingEdit ? <><Spinner animation="border" size="sm" /> Guardando...</> : "Guardar cambios"}</button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(informationEdit)} onHide={() => { if (!savingInformation) setInformationEdit(null); }} centered dialogClassName="person-field-edit-modal">
        <Modal.Header closeButton={!savingInformation}><Modal.Title>{informationEdit ? `Editar ${informationEdit.label.toLowerCase()}` : "Editar información"}</Modal.Title></Modal.Header>
        <Modal.Body>
          {informationEdit?.type === "boolean" ? (
            <label className="person-field-edit-control">
              <span>{informationEdit.label}</span>
              <select value={informationValue} onChange={(event) => setInformationValue(event.target.value)} disabled={savingInformation}>
                <option value="" disabled>Selecciona una respuesta</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </label>
          ) : informationEdit?.options?.length || informationOptions[informationEdit?.field]?.length ? (
            <label className="person-field-edit-control">
              <span>{informationEdit?.label}</span>
              <select value={informationValue ?? ""} onChange={(event) => setInformationValue(event.target.value)} disabled={savingInformation}>
                <option value="">Selecciona una opción</option>
                {(informationEdit.options || informationOptions[informationEdit.field]).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          ) : (
            <label className="person-field-edit-control">
              <span>{informationEdit?.label}</span>
              <input type={informationEdit?.type || "text"} value={informationValue ?? ""} onChange={(event) => setInformationValue(event.target.value)} disabled={savingInformation} />
            </label>
          )}
          {informationError && <Alert variant="danger" className="mt-3">{informationError}</Alert>}
        </Modal.Body>
        <Modal.Footer><button type="button" className="person-secondary-button" disabled={savingInformation} onClick={() => setInformationEdit(null)}>Cancelar</button><button type="button" className="person-primary-button" disabled={savingInformation} onClick={saveInformationField}>{savingInformation ? <><Spinner animation="border" size="sm" /> Guardando...</> : "Guardar"}</button></Modal.Footer>
      </Modal>

    </div>
  );
};

const PersonasPage = ({
  tipos = ["interesada"],
  titulo = "Personas",
  subtitulo = "Registro de personas que han pasado por RedMaestra.",
  basePath = "/personas",
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [profileFilter, setProfileFilter] = useState("todas");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [showInactivePeople, setShowInactivePeople] = useState(false);
  const [inactivePeople, setInactivePeople] = useState(null);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [inactiveError, setInactiveError] = useState("");
  const [inactiveQuery, setInactiveQuery] = useState("");
  const [requestAction, setRequestAction] = useState(null);
  const [requestFeedback, setRequestFeedback] = useState(null);
  const [discardPerson, setDiscardPerson] = useState(null);
  const maestrasMode = tipos.includes("maestra");
  const pageEntity = maestrasMode ? "Maestras" : "Interesadas";
  const scopeQuery = useMemo(() => {
    const params = new URLSearchParams({ tipo: tipos.join(",") });
    return params.toString();
  }, [tipos]);
  const availableFilters = useMemo(
    () => maestrasMode
      ? [...statusFilters, ["whatsapp", "WhatsApp"]]
      : tipos.includes("interesada")
        ? [["todas", "Todas"], ["participante", "Participantes"], ["solicitud", "Solicitudes"], ["otras", "Otras"]]
        : statusFilters,
    [maestrasMode, tipos],
  );
  const filteredInactivePeople = useMemo(() => {
    if (!Array.isArray(inactivePeople)) return [];
    const queryValue = normalize(inactiveQuery.trim());
    if (!queryValue) return inactivePeople;
    return inactivePeople.filter((person) => normalize([
      person.nombre,
      person.rut,
      person.correo,
      person.celular,
    ].filter(Boolean).join(" ")).includes(queryValue));
  }, [inactivePeople, inactiveQuery]);

  useEffect(() => {
    fetch(getApiUrl(`api/personas/?activa=true&${scopeQuery}`), { credentials: "include" })
      .then((activeResponse) => {
        if (!activeResponse.ok) throw new Error();
        return activeResponse.json();
      })
      .then((activeData) => {
        setPeople(Array.isArray(activeData) ? activeData : activeData.results || []);
      })
      .catch(() => setError(`No se pudieron cargar las ${pageEntity.toLocaleLowerCase("es")}.`))
      .finally(() => setLoading(false));
  }, [pageEntity, scopeQuery]);

  const openInactivePeople = async () => {
    setInactiveError("");
    setInactiveQuery("");
    setShowInactivePeople(true);
    if (inactivePeople !== null || inactiveLoading) return;

    setInactiveLoading(true);
    try {
      const response = await fetch(getApiUrl(`api/personas/?activa=false&${scopeQuery}`), {
        credentials: "include",
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      setInactivePeople(Array.isArray(data) ? data : data.results || []);
    } catch (_error) {
      setInactiveError(`No se pudieron cargar las ${pageEntity.toLocaleLowerCase("es")} inactivas.`);
      setInactivePeople(null);
    } finally {
      setInactiveLoading(false);
    }
  };

  useEffect(() => {
    if (!id) { setSelected(null); return; }
    setDetailLoading(true);
    fetch(getApiUrl(`api/personas/${id}/`), { credentials: "include" })
      .then((response) => { if (!response.ok) throw new Error(); return response.json(); })
      .then(setSelected)
      .catch(() => { setError("No se pudo cargar el detalle de la persona."); navigate(basePath, { replace: true }); })
      .finally(() => setDetailLoading(false));
  }, [id, navigate, basePath]);

  const filtered = useMemo(() => {
    const term = normalize(query.trim());
    return people.filter((person) => {
      const roles = (person.roles || []).map(normalize);
      if (profileFilter === "whatsapp" && !person.es_comunidad) return false;
      if (profileFilter === "participante" && !person.es_participante) return false;
      if (profileFilter === "solicitud" && !person.es_solicitud) return false;
      if (profileFilter === "otras" && (person.es_participante || person.es_solicitud)) return false;
      if (
        profileFilter !== "todas"
        && profileFilter !== "whatsapp"
        && profileFilter !== "participante"
        && profileFilter !== "solicitud"
        && profileFilter !== "otras"
        && person.estado !== profileFilter
      ) return false;
      return normalize([
        person.nombre, person.rut, person.correo,
        person.celular, ...roles,
      ].join(" ")).includes(term);
    });
  }, [people, query, profileFilter]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice(page * pageSize, (page + 1) * pageSize);
  useEffect(() => { setPage(0); }, [query, profileFilter, pageSize]);
  useEffect(() => { if (page >= pages) setPage(pages - 1); }, [page, pages]);

  const updateSelectedPerson = (person) => {
    if (!tipos.includes(person.tipo)) {
      setPeople((current) => current.filter((item) => item.id !== person.id));
      setInactivePeople((current) => Array.isArray(current)
        ? current.filter((item) => item.id !== person.id)
        : current);
      setSelected(null);
      const target = person.tipo === "maestra" ? "/maestras" : "/personas";
      navigate(`${target}/${person.id}`, { replace: true });
      return;
    }
    setSelected(person);
    if (person.activa) {
      setPeople((current) => current.some((item) => item.id === person.id)
        ? current.map((item) => item.id === person.id ? { ...item, ...person } : item)
        : [person, ...current]);
      setInactivePeople((current) => Array.isArray(current)
        ? current.filter((item) => item.id !== person.id)
        : current);
    } else {
      setInactivePeople((current) => !Array.isArray(current) ? current : current.some((item) => item.id === person.id)
        ? current.map((item) => item.id === person.id ? { ...item, ...person } : item)
        : [person, ...current]);
      setPeople((current) => current.filter((item) => item.id !== person.id));
    }
  };

  const activateFromList = async (person) => {
    setActivatingId(person.id);
    setInactiveError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/personas/${person.id}/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ activa: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || data.error || "No se pudo activar la persona.");
      setPeople((current) => current.map((item) => item.id === data.id ? { ...item, ...data } : item));
      setPeople((current) => current.some((item) => item.id === data.id) ? current : [data, ...current]);
      setInactivePeople((current) => Array.isArray(current)
        ? current.filter((item) => item.id !== data.id)
        : current);
      if (selected?.id === data.id) setSelected(data);
    } catch (requestError) {
      setInactiveError(requestError.message);
    } finally {
      setActivatingId(null);
    }
  };

  const registerProfileOpening = async (person) => {
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(`api/personas/${person.id}/registrar-apertura/`),
        {
          method: "POST",
          credentials: "include",
          headers: { "X-CSRFToken": csrfToken },
        },
      );
      if (!response.ok) throw new Error(`No se pudo registrar la apertura (${response.status}).`);
      setPeople((current) => current
        .map((item) => item.id === person.id
          ? {
            ...item,
            visitas_30_dias: (Number(item.visitas_30_dias) || 0) + 1,
            ultima_apertura_local: Date.now(),
          }
          : item)
        .sort(orderByProfileOpenings));
    } catch (trackingError) {
      console.error("No se pudo registrar la apertura del perfil:", trackingError);
    }
  };

  const openPersonFromTable = (person) => {
    void registerProfileOpening(person);
    navigate(`${basePath}/${person.id}`);
  };

  const requestUrl = (person, action) => getApiUrl(
    `api/formularios/newsletter-solicitudes/${encodeURIComponent(person.rut)}/${action}/`,
  );

  const runRequestAction = async (person, action, body = {}) => {
    setRequestAction({ personId: person.id, action, deactivate: body.desactivar });
    setRequestFeedback(null);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(requestUrl(person, action), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.detail || "No se pudo completar la acción.");
      if (action === "recordatorio") {
        setPeople((current) => current.map((item) => item.id === person.id ? {
          ...item,
          solicitud: {
            ...item.solicitud,
            correos_enviados: data.correos_enviados ?? ((item.solicitud?.correos_enviados || 0) + 1),
          },
        } : item));
      } else if (action === "aceptar" || body.desactivar) {
        setPeople((current) => current.filter((item) => item.id !== person.id));
      } else if (data.persona) {
        setPeople((current) => current.map((item) => item.id === person.id
          ? { ...item, ...data.persona }
          : item));
      }
      setRequestFeedback({ variant: "success", message: data.message || "Acción completada." });
      return true;
    } catch (requestError) {
      setRequestFeedback({ variant: "danger", message: requestError.message });
      return false;
    } finally {
      setRequestAction(null);
    }
  };

  const sendRequestReminder = (event, person) => {
    event.stopPropagation();
    void runRequestAction(person, "recordatorio");
  };

  const acceptRequest = (event, person) => {
    event.stopPropagation();
    if (!window.confirm(`¿Confirmas que ${person.nombre} debe pasar a ser Maestra?`)) return;
    void runRequestAction(person, "aceptar");
  };

  const openDiscardRequest = (event, person) => {
    event.stopPropagation();
    setDiscardPerson(person);
  };

  const startRequest = async (event, person) => {
    event.stopPropagation();
    if (!window.confirm(`¿Confirmas que quieres iniciar una Solicitud para ${person.nombre}? No se enviará ningún correo automáticamente.`)) return;
    setRequestAction({ personId: person.id, action: "iniciar" });
    setRequestFeedback(null);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/personas/${person.id}/iniciar-solicitud/`), {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.detail || data.error || "No se pudo iniciar la solicitud.");
      if (data.persona) {
        setPeople((current) => current.map((item) => item.id === person.id
          ? { ...item, ...data.persona }
          : item));
      }
      setRequestFeedback({ variant: "success", message: data.message });
    } catch (requestError) {
      setRequestFeedback({ variant: "danger", message: requestError.message });
    } finally {
      setRequestAction(null);
    }
  };

  const discardRequest = async (deactivate) => {
    if (!discardPerson) return;
    const completed = await runRequestAction(discardPerson, "descartar", { desactivar: deactivate });
    if (completed) setDiscardPerson(null);
  };

  return (
    <div className="people-page">
      <section className="people-hero">
        <div><h1>{titulo}</h1><p>{subtitulo}</p></div>
        <div className="people-hero-actions">
          <button type="button" className="people-inactive-button" onClick={openInactivePeople}><FiArchive /> Inactivas {Array.isArray(inactivePeople) && <span>{inactivePeople.length}</span>}</button>
        </div>
      </section>
      {error && <Alert variant="danger">{error}</Alert>}
      {requestFeedback && <Alert variant={requestFeedback.variant} dismissible onClose={() => setRequestFeedback(null)}>{requestFeedback.message}</Alert>}
      <section className="people-toolbar">
        <label className="people-search"><FiSearch /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por nombre, RUT, contacto o perfil..." /></label>
        <output className="people-result-count" aria-live="polite" aria-label={`${filtered.length} ${pageEntity.toLocaleLowerCase("es")} encontradas`}>{filtered.length}</output>
        <div className="people-filter" role="group" aria-label={`Filtrar ${pageEntity.toLocaleLowerCase("es")}`}>
          {availableFilters.map(([value, label]) => <button key={value} type="button" aria-label={`Filtrar por ${label}`} title={label} className={`${profileFilter === value ? "active " : ""}filter-${value}`.trim()} onClick={() => setProfileFilter(value)}>
            <span className="people-filter-label">{label}</span>
            <span className="people-filter-mobile" aria-hidden="true">{value === "whatsapp" ? <FiMessageSquare /> : ["ok", "problema"].includes(value) ? <span className="people-filter-dot" /> : label}</span>
          </button>)}
        </div>
      </section>
      {loading ? <div className="people-loading"><Spinner animation="border" /><span>Cargando {pageEntity.toLocaleLowerCase("es")}...</span></div> : (
        <>
          <div className="people-table-scroll">
            <table className={`people-table${maestrasMode ? "" : " people-table--interested"}`}>
              <thead><tr><th>Nombre y RUT</th>{!maestrasMode && <th className="people-request-heading">Solicitud</th>}{maestrasMode && <th className="people-completion-heading"><span>Completitud</span><small>Ficha</small></th>}</tr></thead>
              <tbody>{visible.map((person) => (
                <tr className={person.estado === "problema" ? "person-row-problema" : "person-row-ok"} key={person.id} onClick={() => openPersonFromTable(person)} tabIndex="0" onKeyDown={(event) => { if (event.key === "Enter") openPersonFromTable(person); }}>
                  <td>
                    <div className="person-table-identity">
                      <div className="person-table-avatar-frame">
                        <div className="person-table-avatar">
                          <PersonAvatar src={person.imagen} name={person.nombre} alt={`Fotografía de ${person.nombre}`} tone={person.es_comunidad ? "comunidad" : person.tipo === "maestra" ? "maestra" : "persona"} />
                        </div>
                        {person.certificacion_sec && <img className="person-sec-mark person-sec-mark--table" src={SecLogo} alt="Certificación SEC" title="Certificación SEC" />}
                      </div>
                      <div className="person-table-copy">
                        <strong>{person.nombre}</strong>
                        <small>{person.rut || "Sin RUT"}</small>
                        {(person.es_participante || person.es_solicitud || person.es_comunidad) && (
                          <div className="person-table-badges">
                            {person.es_participante && <Badge className="person-lifecycle-badge person-lifecycle-badge--participante">Participante</Badge>}
                            {person.es_solicitud && <Badge className="person-lifecycle-badge person-lifecycle-badge--solicitud">Solicitud</Badge>}
                            {person.es_comunidad && <Badge className="person-community-badge">WhatsApp</Badge>}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  {!maestrasMode && <td className="people-request-cell">
                    {person.solicitud ? <div className="people-request-summary">
                      <Badge className={person.solicitud.avanzada_respondida ? "request-status request-status--ready" : "request-status request-status--pending"}>
                        {person.solicitud.avanzada_respondida ? "Respondido" : "Pendiente"}
                      </Badge>
                      <small><FiMail /> {person.solicitud.correos_enviados || 0} correos</small>
                      <div className="people-request-actions">
                        <button type="button" title="Enviar recordatorio" aria-label={`Enviar recordatorio a ${person.nombre}`} disabled={Boolean(requestAction) || person.solicitud.avanzada_respondida || !person.solicitud.tiene_correo} onClick={(event) => sendRequestReminder(event, person)}>
                          {requestAction?.personId === person.id && requestAction.action === "recordatorio" ? <Spinner animation="border" size="sm" /> : <FiMail />}
                        </button>
                        <button type="button" className="request-accept" title="Convertir en Maestra" aria-label={`Convertir a ${person.nombre} en Maestra`} disabled={Boolean(requestAction) || !person.solicitud.avanzada_respondida} onClick={(event) => acceptRequest(event, person)}>
                          {requestAction?.personId === person.id && requestAction.action === "aceptar" ? <Spinner animation="border" size="sm" /> : <FiCheckCircle />}
                        </button>
                        <button type="button" className="request-discard" title="Descartar solicitud" aria-label={`Descartar solicitud de ${person.nombre}`} disabled={Boolean(requestAction)} onClick={(event) => openDiscardRequest(event, person)}><FiXCircle /></button>
                      </div>
                    </div> : <button type="button" className="people-request-start" disabled={Boolean(requestAction)} onClick={(event) => startRequest(event, person)}>
                      {requestAction?.personId === person.id && requestAction.action === "iniciar" ? <Spinner animation="border" size="sm" /> : <FiPlus />} Iniciar
                    </button>}
                  </td>}
                  {maestrasMode && <td className="people-completion-cell">
                    <div className={`people-completion people-completion--${person.completitud >= 80 ? "high" : person.completitud >= 50 ? "medium" : "low"}`} title={`${person.completitud}% de la ficha completada`}>
                      <strong>{person.completitud}%</strong>
                      <span aria-hidden="true"><i style={{ width: `${person.completitud}%` }} /></span>
                    </div>
                  </td>}
                </tr>
              ))}</tbody>
            </table>
            {visible.length === 0 && <div className="people-empty">No hay {pageEntity.toLocaleLowerCase("es")} que coincidan con la búsqueda y el filtro.</div>}
          </div>
          <div className="people-pagination">
            <span>{filtered.length} resultados · Página {page + 1} de {pages}</span>
            <div className="people-pagination-actions">
              <label>Mostrar <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}><option value="10">10</option><option value="20">20</option><option value="50">50</option></select></label>
              <button disabled={page === 0} onClick={() => setPage((value) => value - 1)}><FiChevronLeft /></button><button disabled={page + 1 >= pages} onClick={() => setPage((value) => value + 1)}><FiChevronRight /></button>
            </div>
          </div>
        </>
      )}

      <Modal show={Boolean(id)} onHide={() => navigate(basePath)} size="xl" centered dialogClassName="person-detail-modal">
        <Modal.Header closeButton className="person-modal-topbar" aria-label="Cerrar ficha" />
        <Modal.Body>{detailLoading ? <div className="people-loading"><Spinner animation="border" /></div> : selected && <PersonaDetail person={selected} onPersonUpdate={updateSelectedPerson} />}</Modal.Body>
      </Modal>

      <Modal show={showInactivePeople} onHide={() => setShowInactivePeople(false)} centered size="lg" dialogClassName="inactive-people-modal">
        <Modal.Header closeButton><Modal.Title>{pageEntity} inactivas</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="person-groups-help">Estas {pageEntity} se conservan internamente, pero no aparecen en la tabla ni en los contadores.</p>
          {inactiveError && <Alert variant="danger">{inactiveError}</Alert>}
          {Array.isArray(inactivePeople) && inactivePeople.length > 0 && <label className="inactive-people-search"><FiSearch /><input autoFocus value={inactiveQuery} onChange={(event) => setInactiveQuery(event.target.value)} placeholder="Buscar por nombre, RUT, correo o celular..." /></label>}
          {inactiveLoading ? <div className="people-loading"><Spinner animation="border" /><span>Cargando {pageEntity.toLocaleLowerCase("es")} inactivas...</span></div> : Array.isArray(inactivePeople) && inactivePeople.length === 0 ? <div className="inactive-people-empty"><FiArchive /><span>No hay {pageEntity.toLocaleLowerCase("es")} inactivas.</span></div> : Array.isArray(inactivePeople) && filteredInactivePeople.length === 0 ? <div className="inactive-people-empty"><FiSearch /><span>No hay coincidencias para esta búsqueda.</span></div> : Array.isArray(inactivePeople) && (
            <div className="inactive-people-list">{filteredInactivePeople.map((person) => <article key={person.id}><div><strong>{person.nombre}</strong><span>{person.rut || "Sin RUT"}{person.correo ? ` · ${person.correo}` : ""}</span></div><button type="button" disabled={activatingId === person.id} onClick={() => activateFromList(person)}>{activatingId === person.id ? <Spinner animation="border" size="sm" /> : <FiRotateCcw />} Activar</button></article>)}</div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(discardPerson)} onHide={() => !requestAction && setDiscardPerson(null)} centered dialogClassName="discard-request-modal">
        <Modal.Header closeButton><Modal.Title>Descartar solicitud</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>La solicitud de <strong>{discardPerson?.nombre}</strong> dejará de estar pendiente. ¿Quieres mantener su perfil visible como Interesada o archivarlo?</p>
        </Modal.Body>
        <Modal.Footer className="discard-request-options">
          <button type="button" disabled={Boolean(requestAction)} onClick={() => setDiscardPerson(null)}>Cancelar</button>
          <button type="button" className="keep-visible" disabled={Boolean(requestAction)} onClick={() => discardRequest(false)}>{requestAction?.action === "descartar" && requestAction.deactivate === false ? <Spinner animation="border" size="sm" /> : <FiUser />} Mantener visible</button>
          <button type="button" className="archive-profile" disabled={Boolean(requestAction)} onClick={() => discardRequest(true)}>{requestAction?.action === "descartar" && requestAction.deactivate === true ? <Spinner animation="border" size="sm" /> : <FiArchive />} Archivar</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PersonasPage;
